import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';

const RoleAssignmentModal = ({ user, onClose, onSave }) => {
  const [selectedRole, setSelectedRole] = useState(user?.role || 'player');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const roleOptions = [
    { value: 'scout', label: 'Scout', description: 'Can view and analyze player data' },
    { value: 'club', label: 'Club', description: 'Can manage club operations and transfers' },
    { value: 'player', label: 'Player', description: 'Can manage own profile and performance' },
    { value: 'coach', label: 'Coach', description: 'Can manage coaching records and analysis' },
    { value: 'admin', label: 'Admin', description: 'Full system access and user management' }
  ];

  const handleSave = async () => {
    if (!selectedRole || selectedRole === user?.role) {
      onClose();
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        ?.from('user_profiles')
        ?.update({ 
          role: selectedRole,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', user?.id);

      if (updateError) throw updateError;

      onSave();
    } catch (err) {
      console.error('Error updating user role:', err);
      setError(err?.message || 'Failed to update user role');
    } finally {
      setLoading(false);
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'text-error',
      scout: 'text-accent',
      club: 'text-warning',
      player: 'text-primary',
      coach: 'text-secondary'
    };
    return colors?.[role] || 'text-foreground';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-md w-full shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Shield" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">Change User Role</h2>
              <p className="text-sm text-muted-foreground">Assign new role and permissions</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* User Info */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                {user?.avatar_url ? (
                  <img 
                    src={user?.avatar_url} 
                    alt={user?.full_name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <span className="text-primary font-semibold text-lg">
                    {user?.full_name?.charAt(0)?.toUpperCase()}
                  </span>
                )}
              </div>
              <div>
                <p className="font-semibold text-foreground">{user?.full_name}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Current Role:</span>
              <span className={`text-sm font-semibold ${getRoleColor(user?.role)}`}>
                {user?.role?.charAt(0)?.toUpperCase() + user?.role?.slice(1)}
              </span>
            </div>
          </div>

          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              New Role <span className="text-error">*</span>
            </label>
            <Select
              options={roleOptions}
              value={selectedRole}
              onChange={setSelectedRole}
              placeholder="Select a role"
            />
          </div>

          {/* Warning for Admin Role */}
          {selectedRole === 'admin' && user?.role !== 'admin' && (
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 flex items-start gap-3">
              <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-warning mb-1">Admin Role Warning</p>
                <p className="text-xs text-muted-foreground">
                  Assigning admin role grants full system access. Only one admin can exist at a time. 
                  This action will replace the current admin if one exists.
                </p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-start gap-3">
              <Icon name="XCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-error mb-1">Error</p>
                <p className="text-xs text-muted-foreground">{error}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            loading={loading}
            disabled={loading || selectedRole === user?.role}
            iconName="Save"
            iconPosition="left"
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RoleAssignmentModal;